import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";

const Landing = lazy(() => import("./pages/Landing"));
const CoranPdf = lazy(() => import("./pages/Coran/pdf"));
const RechercheCoran = lazy(() => import("./pages/Recherche"));
const SurahDetail = lazy(() => import("./pages/Coran/pdf/[id]"));
const Names = lazy(() => import("./pages/Names"));
const AudioList = lazy(() => import("./pages/Audio"));
const AudioPlayer = lazy(() => import("./pages/Audio/[id]"));
const Convertisseur = lazy(() => import("./pages/Convertisseur"));
const Tasbih = lazy(() => import("./pages/Tasbih"));
const PrayerTimes = lazy(() => import("./pages/PrayerTimes"));
const Douas = lazy(() => import("./pages/Douas"));
const DouaMatinSoir = lazy(() => import("./pages/Douas/MatinSoir"));
const DouaPriere = lazy(() => import("./pages/Douas/Priere"));
const DouaJoieDetresse = lazy(() => import("./pages/Douas/JoieDetresse"));
const HajjUmrahPage = lazy(() => import("./pages/Douas/HajjUmrah"));
const NaturePage = lazy(() => import("./pages/Douas/Nature"));
const BonComportementPage = lazy(() => import("./pages/Douas/BonComportement"));
const DouaMaisonFamille = lazy(() => import("./pages/Douas/MaisonFamille"));
const MangerBoirePage = lazy(() => import("./pages/Douas/MangerBoire"));
const MaladieMortPage = lazy(() => import("./pages/Douas/MaladieMort"));
const Alphabet = lazy(() => import("./pages/Learn"));
const HadithsPage = lazy(() => import("./pages/Hadiths"));
const RevelationPage = lazy(() => import("./pages/Hadiths/Revelation"));
const LaFoiPage = lazy(() => import("./pages/Hadiths/Lafoi"));
const LaConnaissancePage = lazy(() => import("./pages/Hadiths/Laconnaissance"));
const LesAblutionsPage = lazy(() => import("./pages/Hadiths/Lesablutions"));
const LeBainPage = lazy(() => import("./pages/Hadiths/Lebain"));
const LesMenstruePage = lazy(() => import("./pages/Hadiths/Lesmenstrue"));
const SechePage = lazy(() => import("./pages/Hadiths/Seche"));
const PrierePage = lazy(() => import("./pages/Hadiths/Priere"));
const HorairePage = lazy(() => import("./pages/Hadiths/Horaire"));
const AdhanPage = lazy(() => import("./pages/Hadiths/Adhan"));
const VendrediPage = lazy(() => import("./pages/Hadiths/Vendredi"));
const CraintePage = lazy(() => import("./pages/Hadiths/Crainte"));
const NewDashboard = lazy(() => import("./pages/Dashboards"));
const AidPage = lazy(() => import("./pages/Hadiths/Aid"));
const CalendrierHijri = lazy(() => import("./pages/calendrier"));
const CoranParTheme = lazy(() => import("./pages/CoranParTheme"));
const QiblaCompass = lazy(() => import("./pages/QiblaCompass"));
const Prophetes = lazy(() => import("./pages/Prophetes"));
const Adam = lazy(() => import("./pages/Prophetes/Recit/Adam"));
const Idriss = lazy(() => import("./pages/Prophetes/Recit/Idriss"));
const Noe = lazy(() => import("./pages/Prophetes/Recit/Noe"));
const Houd = lazy(() => import("./pages/Prophetes/Recit/Houd"));
const Salih = lazy(() => import("./pages/Prophetes/Recit/Salih"));
const Ibrahim = lazy(() => import("./pages/Prophetes/Recit/Ibrahim"));
const Lut = lazy(() => import("./pages/Prophetes/Recit/Lut"));
const Ismail = lazy(() => import("./pages/Prophetes/Recit/Ismail"));
const Ishaq = lazy(() => import("./pages/Prophetes/Recit/Ishaq"));
const Yaqub = lazy(() => import("./pages/Prophetes/Recit/Yaqub"));
const Yusuf = lazy(() => import("./pages/Prophetes/Recit/Yusuf"));
const Ayyub = lazy(() => import("./pages/Prophetes/Recit/Ayyub"));
const Shuayb = lazy(() => import("./pages/Prophetes/Recit/Shuayb"));
const Moussa = lazy(() => import("./pages/Prophetes/Recit/Moussa"));
const Haroun = lazy(() => import("./pages/Prophetes/Recit/Haroun"));
const DhulKifl = lazy(() => import("./pages/Prophetes/Recit/DhulKifl"));
const Dawud = lazy(() => import("./pages/Prophetes/Recit/Dawud"));
const Soulayman = lazy(() => import("./pages/Prophetes/Recit/Soulayman"));
const Ilyas = lazy(() => import("./pages/Prophetes/Recit/Ilyas"));
const AlYasa = lazy(() => import("./pages/Prophetes/Recit/AlYasa"));
const Younous = lazy(() => import("./pages/Prophetes/Recit/Younous"));
const Zakariya = lazy(() => import("./pages/Prophetes/Recit/Zakariya"));
const Yahya = lazy(() => import("./pages/Prophetes/Recit/Yahya"));
const Issa = lazy(() => import("./pages/Prophetes/Recit/Issa"));
const Muhammad = lazy(() => import("./pages/Prophetes/Recit/Muhammad"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));

// 🌿 Écran affiché brièvement pendant le chargement d'une page,
// dans les couleurs de l'app (émeraude/or) au lieu d'un écran blanc.
const Chargement = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#faf6ec",
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        border: "4px solid #f1e9d6",
        borderTopColor: "#c9a227",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const App = () => {
  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<Chargement />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/recherche" element={<RechercheCoran />} />
          <Route path="/coran/pdf" element={<CoranPdf />} />
          <Route path="/coran/pdf/:id" element={<SurahDetail />} />
          <Route path="/names" element={<Names />} />
          <Route path="/audio" element={<AudioList />} />
          <Route path="/audio/:id" element={<AudioPlayer />} />
          <Route path="/convertisseur" element={<Convertisseur />} />
          <Route path="/tasbih" element={<Tasbih />} />
          <Route path="/prayer-times" element={<PrayerTimes />} />
          <Route path="/douas" element={<Douas />} />
          <Route path="/alphabet" element={<Alphabet />} />
          <Route path="/douas/matin-soir" element={<DouaMatinSoir />} />
          <Route path="/douas/priere" element={<DouaPriere />} />
          <Route path="/douas/joie-detresse" element={<DouaJoieDetresse />} />
          <Route path="/douas/hajj-umrah" element={<HajjUmrahPage />} />
          <Route path="/douas/nature" element={<NaturePage />} />
          <Route path="/douas/bon-comportement" element={<BonComportementPage />} />
          <Route path="/douas/maison-famille" element={<DouaMaisonFamille />} />
          <Route path="/douas/manger-boire" element={<MangerBoirePage />} />
          <Route path="/douas/maladie-mort" element={<MaladieMortPage />} />
          <Route path="/hadiths" element={<HadithsPage />} />
          <Route path="/hadiths/la-revelation" element={<RevelationPage />} />
          <Route path="/hadiths/la-foi" element={<LaFoiPage />} />
          <Route path="/hadiths/la-connaissance" element={<LaConnaissancePage />} />
          <Route path="/hadiths/les-ablutions-wudu" element={<LesAblutionsPage />} />
          <Route path="/hadiths/le-bain-rituel-ghusl" element={<LeBainPage />} />
          <Route path="/hadiths/les-menstruations" element={<LesMenstruePage />} />
          <Route path="/hadiths/tayammum-ablution-seche" element={<SechePage />} />
          <Route path="/hadiths/la-priere-salat" element={<PrierePage />} />
          <Route path="/hadiths/les-horaires-de-priere" element={<HorairePage />} />
          <Route path="/hadiths/la-priere-de-crainte" element={<CraintePage />} />
          <Route path="/hadiths/les-prieres-de-laid" element={<AidPage />} />
          <Route path="/progression" element={<NewDashboard />} />
          <Route path="/calendrier" element={<CalendrierHijri />} />
          <Route path="/coran/theme" element={<CoranParTheme />} />
          <Route path="/qibla" element={<QiblaCompass />} />
          <Route path="/prophetes" element={<Prophetes />} />
          <Route path="/prophetes/recit/adam" element={<Adam />} />
          <Route path="/prophetes/recit/idriss" element={<Idriss />} />
          <Route path="/prophetes/recit/noe" element={<Noe />} />
          <Route path="/prophetes/recit/houd" element={<Houd />} />
          <Route path="/prophetes/recit/salih" element={<Salih />} />
          <Route path="/prophetes/recit/ibrahim" element={<Ibrahim />} />
          <Route path="/prophetes/recit/lut" element={<Lut />} />
          <Route path="/prophetes/recit/ismail" element={<Ismail />} />
          <Route path="/prophetes/recit/ishaq" element={<Ishaq />} />
          <Route path="/prophetes/recit/yaqub" element={<Yaqub />} />
          <Route path="/prophetes/recit/yusuf" element={<Yusuf />} />
          <Route path="/prophetes/recit/ayyub" element={<Ayyub />} />
          <Route path="/prophetes/recit/shuayb" element={<Shuayb />} />
          <Route path="/prophetes/recit/moussa" element={<Moussa />} />
          <Route path="/prophetes/recit/haroun" element={<Haroun />} />
          <Route path="/prophetes/recit/dhul-kifl" element={<DhulKifl />} />
          <Route path="/prophetes/recit/dawud" element={<Dawud />} />
          <Route path="/prophetes/recit/soulayman" element={<Soulayman />} />
          <Route path="/prophetes/recit/ilyas" element={<Ilyas />} />
          <Route path="/prophetes/recit/al-yasa" element={<AlYasa />} />
          <Route path="/prophetes/recit/younous" element={<Younous />} />
          <Route path="/prophetes/recit/zakariya" element={<Zakariya />} />
          <Route path="/prophetes/recit/yahya" element={<Yahya />} />
          <Route path="/prophetes/recit/issa" element={<Issa />} />
          <Route path="/prophetes/recit/muhammad" element={<Muhammad />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;